/**
 * ============================================================================
 * RecurringDiscountsSection - Displays All Courts with Recurring Discounts
 * ============================================================================
 *
 * Section component that shows all courts that have recurring booking
 * discounts configured, with helpful info banner.
 *
 * ============================================================================
 */

import React from 'react';
import { Info, Calendar } from 'lucide-react';
import { RecurringDiscountCard } from './RecurringDiscountCard';
import type { Court } from '../../types/courts';

interface RecurringDiscountsSectionProps {
  courts: Court[];
}

export function RecurringDiscountsSection({ courts }: RecurringDiscountsSectionProps) {
  // Filter courts that have recurring bookings enabled
  const courtsWithRecurring = courts.filter((court) => court.allow_recurring_bookings);

  if (courtsWithRecurring.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              🔁 Recurring Booking Discounts
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              No courts have recurring booking discounts configured yet.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-2">How Recurring Discounts Work:</p>
                  <ul className="space-y-1 ml-4 list-disc">
                    <li>
                      Customers book the same time slot weekly for 1, 3, 6, or 12 months
                    </li>
                    <li>
                      They get automatic discounts based on commitment length
                    </li>
                    <li>Great for filling regular time slots and building loyal customers</li>
                    <li>Configure discounts per court in the Courts tab</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Section Header */}
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-blue-600" />
          🔁 Recurring Booking Discounts ({courtsWithRecurring.length}{' '}
          {courtsWithRecurring.length === 1 ? 'court' : 'courts'})
        </h3>
        <p className="text-sm text-gray-600">
          These discounts apply when customers book recurring sessions (weekly commitment for 1-12
          months)
        </p>
      </div>

      {/* Info Banners */}
      <div className="space-y-2 sm:space-y-3 mb-4">
        {/* Configuration Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-[10px] sm:text-sm text-blue-900">
              <p className="font-semibold mb-1">About Recurring Bookings:</p>
              <p>
                Recurring bookings allow customers to reserve the same time slot every week for
                extended periods. The discount percentages below are configured per court and
                automatically applied based on the commitment length. To edit these settings, visit
                the Courts tab.
              </p>
            </div>
          </div>
        </div>

        {/* Discount Policy Info */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 sm:p-3">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-[10px] sm:text-sm text-amber-900">
              <p className="font-semibold mb-1">📋 Discount Policy:</p>
              <p>
                Recurring discounts apply to recurring bookings only. Time-based promotions apply to
                one-time bookings only. Customers should compare both options to find their best deal.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Courts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courtsWithRecurring.map((court) => (
          <RecurringDiscountCard key={court.id} court={court} />
        ))}
      </div>

      {/* Empty State Message if all courts are inactive */}
      {courtsWithRecurring.length > 0 &&
        courtsWithRecurring.every((court) => !court.is_active) && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-900 text-center">
              ⚠️ All courts with recurring discounts are currently inactive
            </p>
          </div>
        )}
    </div>
  );
}
